import { faCircleCheck, faDownload } from "@fortawesome/pro-duotone-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AlertTitle, Box, Button, CircularProgress, Dialog, DialogContent, DialogTitle, IconButton, LinearProgress, Paper, styled, Tooltip, TooltipProps, Typography, useTheme } from "@mui/material";
import { IFormSubmit, IFormValidate, ValidateOn } from "@ngt/forms-core";
import { FileUpload, Form, FormSubmitButton, GroupedField, GroupedFields, IFormLabel, IFormValidationError, ILookup, OnlinePatientManagementContext, useSnackbar, ValidationErrorType } from "@ngt/opms";
import { FC, useCallback, useContext, useEffect, useState } from "react";
import { GetMriImageUploadById, MriImageUpload, PostSubmitImageUpload } from "../../api/dtos";
import { AppSettings } from "../../AppSettings";
import { ImageUploadUpdaterContext } from "../../contexts/ImageUploadUpdaterContext";
import MriImageUploadsContext from "../../hooks/context/MriImageUploadsContext";
import useGetImageUploadById from "../../hooks/data/useGetImageUploadById";


// const RedFontAwesomeIconNoHover = styled(FontAwesomeIcon)(({theme}) => ({
//     color: theme.palette.mode === 'dark' ?
//     `${AppSettings.palette.redLight} !important` :
//     `${AppSettings.palette.redDark} !important`, 
// }));

const GreenFontAwesomeIconNoHover = styled(FontAwesomeIcon)(({theme}) => ({
    color: theme.palette.mode === 'dark' ?
    `${AppSettings.palette.greenLight} !important` :
    `${AppSettings.palette.greenDark} !important`, 
}));

// const BlueFontAwesomeIconNoHover = styled(FontAwesomeIcon)(({theme}) => ({
//     color: theme.palette.mode === 'dark' ?
//     `${AppSettings.palette.blueLight} !important` :
//     `${AppSettings.palette.blueDark} !important`, 
// }));

const StyledButtonGroup = styled(Box)(({ theme }) => ({
    padding: theme.spacing(2, 2, 2, 2),
    textAlign: 'center',
    gap: theme.spacing(2),
}))

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
    padding: theme.spacing(2),

    '&>p': {
        padding: theme.spacing(0),
        fontSize: '1.25rem',
        fontWeight: 500
    }
}))

const StyledDialogContent = styled(DialogContent)(({theme}) => ({
    padding: theme.spacing(0, 0),
    overflow: 'visible',

    '&>p': {
        padding: `${theme.spacing(0, 2, 2, 2)} !important`,
    }
}));

const LoadingTypography = styled(Typography)(() => ({
    display: 'flex',
    alignItems: 'center'

}));

// #1e1e1e

const StyledBox = styled(Box)(() => ({
    display: 'flex', 
    alignItems: 'center', 
    gap: 16, 
    height: '100%',
    padding: 16,
}));

const StyledBoxSpaceBetween = styled(Box)(() => ({
    display: 'flex', 
    justifyContent: 'space-between',
    alignItems: 'center', 
    height: '100%',
    padding: 16,
}));



const StyledTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ tooltip: className }} />
))(({ theme }) => ({
    
    backgroundColor: theme.palette.mode === 'dark' ?
        `rgba(0, 0, 0, 0.87)` :
        theme.palette.common.white,
    color: theme.palette.mode === 'dark' ?
        theme.palette.common.white :
        `rgba(0, 0, 0, 0.87)`,
    boxShadow: theme.shadows[1],
    fontSize: 11,
    
}));

const StyledButton = styled(IconButton)(({theme}) => ({
    padding: theme.spacing(0),
    color: theme.palette.mode === 'dark' ?
        `${AppSettings.palette.primaryDark} !important` :
        `${AppSettings.palette.primaryLight} !important`, 

    '&:hover': {
        color: theme.palette.mode === 'dark' ?
        `${theme.palette.primary.light} !important` :
        `${theme.palette.primary.dark} !important`, 
        background: 'none',
    },

    '&:last-of-type': {
        marginRight: theme.spacing(0)
    }
}));

const StyledA = styled("a")(({theme}) => ({
    color: theme.palette.mode === 'dark' ?
        `${AppSettings.palette.primaryDark} !important` :
        `${AppSettings.palette.primaryLight} !important`, 

    '&:hover': {
        color: theme.palette.mode === 'dark' ?
        `${theme.palette.primary.light} !important` :
        `${theme.palette.primary.dark} !important`, 
        background: 'none',
    },
}));


interface IImageUploadDialog {
    displayImage?: boolean;
    displayCem?: boolean;
    dialogOpen: boolean;
    setDialogOpen: (open: boolean) => void;
    imageUploadId: number | undefined;
    setImageUploadId: (mriImageUploadId: number | undefined) => void;
}

const ImageUploadDialog: FC<IImageUploadDialog> = ({
    displayImage,
    displayCem,
    dialogOpen,
    setDialogOpen,
    imageUploadId: tempId,
    setImageUploadId
}) => {
    const theme = useTheme();
    
    const { serviceStackClient: client } = useContext(OnlinePatientManagementContext);
    const { enqueueSnackbar } = useSnackbar();

    const { data: imageUpload, loading } = useGetImageUploadById(tempId);

    const [updatedImageUpload, setUpdatedImageUpload] = useState<MriImageUpload | undefined>(imageUpload);

    const imageUploadUpdater = useContext(ImageUploadUpdaterContext)

    useEffect(() => {
        if (imageUpload) {
            setUpdatedImageUpload(imageUpload);
        }
    }, [imageUpload]);
    
    useEffect(() => {
        if (!imageUpload?.imageDocument?.id) {
            return;
        }

        const unsubscribe = imageUploadUpdater.subscribe(imageUpload.imageDocument.id, async () => {
            //load new info for image upload
            const response = await client.get(
                new GetMriImageUploadById({
                    id: imageUpload.id,
                })
            )
            
            setUpdatedImageUpload(response.data);
        });

        return () => {
            unsubscribe();
        }
    }, [imageUploadUpdater, imageUpload?.imageDocument?.id]);

    useEffect(() => {
        if (!imageUpload?.cemDocument?.id) {
            return;
        }

        const unsubscribe = imageUploadUpdater.subscribe(imageUpload.cemDocument.id, async () => {
            //load new info for image upload
            const response = await client.get(
                new GetMriImageUploadById({
                    id: imageUpload.id,
                })
            )
            
            setUpdatedImageUpload(response.data);
        });

        return () => {
            unsubscribe();
        }
    }, [imageUploadUpdater, imageUpload?.cemDocument?.id]);

    

    const { refresh, mutate } = useContext(MriImageUploadsContext);

    const closeDialog = useCallback(() => {
        setDialogOpen(false);
        // setImageUploadId(undefined);
    }, [setDialogOpen, setImageUploadId]);


    const [formSaving, setFormSaving] = useState(false); 

    const onImageUploadSubmit: IFormSubmit<MriImageUpload, IFormValidationError> = useCallback(async (form) => {
        
        setFormSaving(true);

        if (!form.value) {
            throw new Error("Form data is missing.");
        }

        try {
            let mriImageUpload = {...form.value};

            if(!displayImage) {
                mriImageUpload.imageDocument = undefined;
            }

            if(!displayCem) {
                mriImageUpload.cemDocument = undefined;
            }

            await client.post(
                new PostSubmitImageUpload({
                    mriImageUpload: {...mriImageUpload},
                    
                })
            );

            await mutate(undefined, { revalidate: true });

            setFormSaving(false);

            closeDialog();

            enqueueSnackbar(
                <>
                    <AlertTitle>
                        Upload Successful
                    </AlertTitle>
                    This upload will be available after it has been de-identified.
                </>,
                { variant: 'success' }
            );
        }
        catch (error: any) {
            setFormSaving(false);

            enqueueSnackbar(
                <>
                    <AlertTitle>
                        Upload Failed
                    </AlertTitle>
                    An error occurred while attempting to upload the file.
                </>,
                { variant: 'critical' }
            );
        }
    
    }, [client, enqueueSnackbar, closeDialog, setFormSaving, setDialogOpen, refresh, displayCem, displayImage]);

    
    
        const imageUploadValidate: IFormValidate<MriImageUpload, IFormValidationError> = useCallback(async (formState) => {
    
            let errors: Record<string, IFormValidationError[]> = {};
            
            if (formState.value?.imageDocument !== undefined) {
                // if (formState.value.imageDocument.name === undefined) {
                //     errors['imageDocument.name'] = [
                //         {
                //             property: 'imageDocument.name',
                //             code: 'IU-001',
                //             message: 'This field is required.',
                //             detailedMessage: 'This field is required.',
                //             type: ValidationErrorType.Normal
                //         }
                //     ];
                // }
                if (formState.value.imageDocument.file === undefined) {
                    errors['imageDocument.file'] = [
                        {
                            property: 'imageDocument.file',
                            code: 'IU-002',
                            message: 'This field is required.',
                            detailedMessage: 'This field is required.',
                            type: ValidationErrorType.Normal
                        }
                    ];
                }
            }
            if (formState.value?.cemDocument !== undefined) {
                // if (formState.value.cemDocument.name === undefined) {
                //     errors['cemDocument.name'] = [
                //         {
                //             property: 'cemDocument.name',
                //             code: 'IU-003',
                //             message: 'This field is required.',
                //             detailedMessage: 'This field is required.',
                //             type: ValidationErrorType.Normal
                //         }
                //     ];
                // }
                if (formState.value.cemDocument.file === undefined) {
                    errors['cemDocument.file'] = [
                        {
                            property: 'cemDocument.file',
                            code: 'IU-004',
                            message: 'This field is required.',
                            detailedMessage: 'This field is required.',
                            type: ValidationErrorType.Normal
                        }
                    ];
                }
            }
    
            return errors;
        }, []);

        const imageUploadFieldsLookups: ILookup[] = [
        ];
        
        const imageUploadFieldsLabels: IFormLabel[] = [
            // {
            //     propertyPath: 'imageDocument.name',
            //     label: 'Name',
            //     detailedLabel: 'Name',
            // },
            {
                propertyPath: 'imageDocument.file',
                label: 'Upload',
                detailedLabel: 'Upload',
            },
            // {
            //     propertyPath: 'cemDocument.name',
            //     label: 'Name',
            //     detailedLabel: 'Name',
            // },
            {
                propertyPath: 'cemDocument.file',
                label: 'Upload',
                detailedLabel: 'Upload',
            },
        ];


    return <>
        <Dialog
            open={dialogOpen === true}
            onClose={closeDialog}
            aria-labelledby="start-dialog-title"
            aria-describedby="start-dialog-description"
            maxWidth="md"
            fullWidth
        >
            <Paper>
                <StyledDialogTitle id="start-dialog-title">
                    <Typography
                        variant="h3"
                    >
                        Upload {displayImage ? "MRI" : displayCem ? "CEM" : ""} {!(updatedImageUpload === undefined || loading == true) && <>(Participant {updatedImageUpload?.participantNumber})</>}
                    </Typography>
                </StyledDialogTitle>
                <StyledDialogContent>


                    {
                        updatedImageUpload === undefined || loading == true ? (
                            <LinearProgress
                                sx={{
                                    width: '100%',
                                    margin: theme.spacing(2, 0),
                                }}
                            />
                        ) : (
                            <>
                                <Form
                                    initialValue={updatedImageUpload}
                                    onSubmit={onImageUploadSubmit}   
                                    lookups={imageUploadFieldsLookups}
                                    labels={imageUploadFieldsLabels}
                                    validateOn={ValidateOn.onChange} 
                                    onValidate={imageUploadValidate}    
                                    debug={AppSettings.debug}
                                >
                                    {
                                        displayImage && (
                                            <GroupedFields>
                                                {
                                                    updatedImageUpload?.imageDocument?.isVerified !== undefined ? (
                                                        <StyledBoxSpaceBetween>
                                                            <StyledTooltip title={`Download ${updatedImageUpload?.imageDocument?.name}`}>
                                                                <StyledBox>
                                                                    <LoadingTypography variant="body1">
                                                                        {updatedImageUpload?.imageDocument?.name}
                                                                    </ LoadingTypography>
                                                                    <StyledButton
                                                                        size="medium"
                                                                    >
                                                                        <StyledA href={`/opms/file/download/${updatedImageUpload?.imageDocument?.file?.id}`}>
                                                                            <FontAwesomeIcon icon={faDownload} fixedWidth />
                                                                        </StyledA>
                                                                    </StyledButton>
                                                                </ StyledBox>
                                                            </StyledTooltip>
                                                            <Box
                                                                sx={{
                                                                    marginLeft: 'auto',
                                                                }}
                                                            />
                                                            {
                                                                updatedImageUpload?.imageDocument?.isVerified === true ? (
                                                                    <StyledTooltip title={`This upload has been verified. No changes can be made.`}>
                                                                        <StyledBox>
                                                                            <GreenFontAwesomeIconNoHover icon={faCircleCheck} size="xl"/>
                                                                            <LoadingTypography variant="body1">
                                                                                Verified
                                                                            </ LoadingTypography>
                                                                        </ StyledBox>
                                                                    </StyledTooltip>
                                                                ) : (
                                                                    <StyledTooltip title={`This upload is Verifying.`}>
                                                                        <StyledBox>
                                                                            <CircularProgress size="25px"/>
                                                                            <LoadingTypography variant="body1">
                                                                                Verifying...
                                                                            </ LoadingTypography>
                                                                        </ StyledBox>
                                                                    </StyledTooltip>
                                                                )
                                                            }
                                                            {/* {
                                                                updatedImageUpload?.imageDocument?.isUploadedToSharePoint !== undefined && (
                                                                    <>
                                                                        {
                                                                            updatedImageUpload?.imageDocument?.isUploadedToSharePoint === true ? (
                                                                                <StyledTooltip title={`Uploaded to SharePoint`}>
                                                                                    <StyledBox>
                                                                                        <BlueFontAwesomeIconNoHover icon={faCircleCheck} size="xl" fixedWidth />
                                                                                        <LoadingTypography variant="body1">
                                                                                            Uploaded
                                                                                        </ LoadingTypography>
                                                                                    </StyledBox>
                                                                                </StyledTooltip>
                                                                            ) : (
                                                                                <StyledTooltip title={`Uploading to SharePoint`}>
                                                                                    <StyledBox>
                                                                                        <CircularProgress size="25px"/>
                                                                                        <LoadingTypography variant="body1">
                                                                                            Uploading...
                                                                                        </ LoadingTypography>
                                                                                    </ StyledBox>
                                                                                </StyledTooltip>
                                                                            )
                                                                        }
                                                                    </>
                                                                )
                                                            } */}
                                                            {/* {
                                                                updatedImageUpload?.imageDocument?.uploadToSharePointFailed === true && (
                                                                    <StyledTooltip title={`Upload to SharePoint failed, to re-try, please click the re-upload button in the main page table.`}>
                                                                        <StyledBox>
                                                                            <RedFontAwesomeIconNoHover icon={faCircleX} color="red" size="xl" fixedWidth />
                                                                            <LoadingTypography variant="body1">
                                                                                SharePoint Upload Failed
                                                                            </ LoadingTypography>
                                                                        </StyledBox>
                                                                    </StyledTooltip>
                                                                )
                                                            } */}
                                                        </ StyledBoxSpaceBetween>
                                                    ) : (
                                                        <>
                                                            {
                                                                updatedImageUpload?.imageDocument?.isDeIdentified === false ? (
                                                                    <StyledTooltip title={`This upload is being de-identified, you can't make any changes until this is done.`}>
                                                                        <StyledBox>
                                                                            <CircularProgress size="25px"/>
                                                                            <LoadingTypography variant="body1">
                                                                                De-identifying...
                                                                            </ LoadingTypography>
                                                                        </ StyledBox>
                                                                    </StyledTooltip>
                                                                ) : (
                                                                    <>
                                                                        {/* <GroupedField
                                                                            propertyPath={"imageDocument.name"}
                                                                            component={Text}
                                                                            FieldGridProps={{
                                                                                size: {
                                                                                    xs: 12,
                                                                                    sm: 12,
                                                                                    md: 6,
                                                                                    lg: 6,
                                                                                    xl: 6
                                                                                }
                                                                            }}
                                                                        /> */}
                                                                        <GroupedField
                                                                            propertyPath={"imageDocument.file"}
                                                                            component={FileUpload}
                                                                            FieldGridProps={{
                                                                                size: {
                                                                                    xs: 12,
                                                                                    sm: 12,
                                                                                    md: 6,
                                                                                    lg: 6,
                                                                                    xl: 6
                                                                                }
                                                                            }}
                                                                        />
                                                                    </>
                                                                )
                                                            }
                                                        </>
                                                    )
                                                }
                                            </GroupedFields>
                                        )
                                    }
                
                                    {
                                        updatedImageUpload?.mammogramPerformed === true && displayCem && (
                                            <GroupedFields>                    
                                                {
                                                    updatedImageUpload?.cemDocument?.isVerified === true ? (
                                                        <StyledBoxSpaceBetween>
                                                            <StyledTooltip title={`Download ${updatedImageUpload?.cemDocument?.name}`}>
                                                                <StyledBox>
                                                                    <LoadingTypography variant="body1">
                                                                        {updatedImageUpload?.cemDocument?.name}
                                                                    </ LoadingTypography>
                                                                    <StyledButton
                                                                        size="medium"
                                                                    >
                                                                        <StyledA href={`/opms/file/download/${updatedImageUpload?.cemDocument?.file?.id}`}>
                                                                            <FontAwesomeIcon icon={faDownload} fixedWidth />
                                                                        </StyledA>
                                                                    </StyledButton>
                                                                </StyledBox>
                                                            </StyledTooltip>
                                                            <Box
                                                                sx={{
                                                                    marginLeft: 'auto',
                                                                }}
                                                            />
                                                            <StyledTooltip title={`This upload has been verified. No changes can be made.`}>
                                                                <StyledBox>
                                                                    <GreenFontAwesomeIconNoHover icon={faCircleCheck} size="xl"/>
                                                                    <LoadingTypography variant="body1">
                                                                        Verified
                                                                    </ LoadingTypography>
                                                                </ StyledBox>
                                                            </StyledTooltip>
                                                            {/* {
                                                                updatedImageUpload?.cemDocument?.isUploadedToSharePoint !== undefined && (
                                                                    <>
                                                                        {
                                                                            updatedImageUpload?.cemDocument?.isUploadedToSharePoint === true ? (
                                                                                <StyledTooltip title={`Uploaded to SharePoint`}>
                                                                                    <StyledBox>
                                                                                        <BlueFontAwesomeIconNoHover icon={faCircleCheck} size="xl" fixedWidth />
                                                                                        <LoadingTypography variant="body1">
                                                                                            Uploaded
                                                                                        </ LoadingTypography>
                                                                                    </StyledBox>
                                                                                </StyledTooltip>
                                                                            ) : (
                                                                                <StyledTooltip title={`Uploading to SharePoint`}>
                                                                                    <StyledBox>
                                                                                        <CircularProgress size="25px"/>
                                                                                        <LoadingTypography variant="body1">
                                                                                            Uploading...
                                                                                        </ LoadingTypography>
                                                                                    </ StyledBox>
                                                                                </StyledTooltip>
                                                                            )
                                                                        }
                                                                    </>
                                                                )
                                                            }
                                                            {
                                                                updatedImageUpload?.cemDocument?.uploadToSharePointFailed === true && (
                                                                    <StyledTooltip title={`Upload to SharePoint failed, to re-try, please click the re-upload button in the main page table.`}>
                                                                        <StyledBox>
                                                                            <RedFontAwesomeIconNoHover icon={faCircleX} size="xl" fixedWidth />
                                                                            <LoadingTypography variant="body1">
                                                                                SharePoint Upload Failed
                                                                            </ LoadingTypography>
                                                                        </StyledBox>
                                                                    </StyledTooltip>
                                                                )
                                                            } */}
                                                        </ StyledBoxSpaceBetween>
                                                    ) : (
                                                        <>
                                                            {
                                                                updatedImageUpload?.cemDocument?.isDeIdentified === false ? (
                                                                    <StyledTooltip title={`This upload is being de-identified, you can't make any changes until this is done.`}>
                                                                        <StyledBox>
                                                                            <CircularProgress size="25px"/>
                                                                            <LoadingTypography variant="body1">
                                                                                De-identifying...
                                                                            </ LoadingTypography>
                                                                        </ StyledBox>
                                                                    </StyledTooltip>
                                                                ) : (
                                                                    <>
                                                                        {/* <GroupedField
                                                                            propertyPath={"cemDocument.name"}
                                                                            component={Text}
                                                                            FieldGridProps={{
                                                                                size: {
                                                                                    xs: 12,
                                                                                    sm: 12,
                                                                                    md: 6,
                                                                                    lg: 6,
                                                                                    xl: 6
                                                                                }
                                                                            }}
                                                                        /> */}
                                                                        <GroupedField
                                                                            propertyPath={"cemDocument.file"}
                                                                            component={FileUpload}
                                                                            FieldGridProps={{
                                                                                size: {
                                                                                    xs: 12,
                                                                                    sm: 12,
                                                                                    md: 6,
                                                                                    lg: 6,
                                                                                    xl: 6
                                                                                }
                                                                            }}
                                                                        />
                                                                    </>
                                                                )
                                                            }
                                                        </>
                                                    )
                                                }
                                            </GroupedFields>
                                        )
                                    }
                                    
                                    
                                    <StyledButtonGroup
                                        display="flex"
                                        justifyContent="flex-end"
                                    >
                                        <Button
                                            color="secondary"
                                            onClick={closeDialog}
                                            loading={formSaving}  
                                        >
                                            Cancel
                                        </Button>
                
                                        <FormSubmitButton
                                            color="primary"   
                                            variant="contained"     
                                            loading={formSaving}                                    
                                        >
                                            Submit
                                        </FormSubmitButton>
                
                                    </StyledButtonGroup>
                                </Form>
                            </>
                        )
                    }
                </StyledDialogContent>
            </Paper>
        </Dialog>
    </>
}

export default ImageUploadDialog;